// LWI-1721 — informational UI context sent with every API request so the
// Audit Log can say where a change came from:
//   X-Client-Screen  page name (document.title without the app suffix)
//   X-Client-Route   matched react-router PATTERN (never the concrete URL)
//   X-Client-Action  label of the control clicked just before a mutating call
// Only text already visible on screen is used; no input values are read.

export const SCREEN_MAX = 120;
export const ROUTE_MAX = 200;
export const ACTION_MAX = 120;
export const ACTION_TTL_MS = 3000;

// App.jsx sets Helmet titleTemplate "%s | IQ" and defaultTitle "LOGIKweb-IQ".
const TITLE_SUFFIX = / \| IQ$/;
const DEFAULT_TITLE = "LOGIKweb-IQ";
const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const CLICKABLE =
  '[data-audit-action], button, [role="button"], [role="menuitem"], a, [type="submit"]';
// A control wrapping block content (a clickable card or row) has no short
// visible label, so the text fallback is skipped for it.
const BLOCK_CONTENT =
  "div, p, ul, ol, li, table, section, article, form, h1, h2, h3, h4, h5, h6";

let currentRoute = "";
// { label, at } — the label is taken at click time (attributes + textContent,
// no layout), so a button that re-renders to "Saving…" still logs "Save", and
// no element is kept alive after the click.
let lastAction = null;

const clean = (value, max) => {
  const text = String(value ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, " ")
    // A lone surrogate would make encodeURIComponent throw.
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "�")
    .replace(/\s+/g, " ")
    .trim();
  // Cap by characters (code points), never splitting a surrogate pair.
  return text.length > max ? Array.from(text).slice(0, max).join("") : text;
};

// Route patterns from a react-router matches array: join each match's own
// path (an absolute child path restarts the pattern).
export function patternFromMatches(matches = []) {
  let pattern = "";
  matches.forEach((m) => {
    const path = m?.route?.path;
    if (!path) return;
    pattern = path.startsWith("/")
      ? path
      : `${pattern.replace(/\/$/, "")}/${path}`;
  });
  return pattern.replace(/\/+/g, "/").replace(/(.)\/$/, "$1") || "";
}

export function setCurrentRoute(pattern) {
  currentRoute = clean(pattern, ROUTE_MAX);
}

export const getCurrentRoute = () => currentRoute;

export function getScreen(doc = typeof document !== "undefined" ? document : null) {
  const title = clean(doc?.title, SCREEN_MAX + 10).replace(TITLE_SUFFIX, "");
  if (!title || title === DEFAULT_TITLE) return "";
  return clean(title, SCREEN_MAX);
}

// data-audit-action, then aria-label, then title; visible text (textContent,
// no reflow) only for short controls without block content.
export function labelFor(el) {
  if (!el) return "";
  const named =
    el.getAttribute?.("data-audit-action") ||
    el.getAttribute?.("aria-label") ||
    el.getAttribute?.("title");
  if (named && clean(named, ACTION_MAX)) return clean(named, ACTION_MAX);
  if (el.querySelector?.(BLOCK_CONTENT)) return "";
  const text = clean(el.textContent, Infinity);
  return text.length > ACTION_MAX ? "" : text;
}

export function recordClick(event) {
  const target = event?.target;
  const el = target?.closest?.(CLICKABLE);
  if (!el) return;
  const label = labelFor(el);
  lastAction = label ? { label, at: Date.now() } : null;
}

// Takes the last clicked label if it is fresh, and clears it.
export function consumeAction(now = Date.now()) {
  const a = lastAction;
  lastAction = null;
  return a && now - a.at <= ACTION_TTL_MS ? a.label : "";
}

// Values are cleaned and capped by characters first, then percent-encoded:
// XHR setRequestHeader throws on characters above U+00FF and Latin-1 would be
// mangled server-side. The backend decodes once.
const encode = (value) => encodeURIComponent(value);

// Returns the headers to attach for a request with this HTTP method.
export function clientContextHeaders(method = "get") {
  const headers = {};
  const screen = getScreen();
  if (screen) headers["X-Client-Screen"] = encode(screen);
  if (currentRoute) headers["X-Client-Route"] = encode(currentRoute);
  if (MUTATING.has(String(method).toUpperCase())) {
    const action = consumeAction();
    if (action) headers["X-Client-Action"] = encode(action);
  }
  return headers;
}

let installed = false;
// One capture-phase listener for the whole app; safe to call repeatedly.
export function installClickCapture(doc = document) {
  if (installed) return;
  installed = true;
  doc.addEventListener("click", recordClick, true);
}

export function resetClientContext() {
  currentRoute = "";
  lastAction = null;
  installed = false;
}
